import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { useEditor, useValue } from 'tldraw';
import { useGeminiLanguage } from '../hooks/useGeminiLanguage';
import { useBoardStore } from '../store/useBoardStore';
import { getCanvasMessages } from '../utils/canvasI18n';
import { objectProcessingLabel } from '../utils/objectProcessing';

const LABEL_GAP_SCREEN = 10;
const VIEW_PAD = 48;

function resolveProcessingShape(editor, entry) {
  if (!editor || !entry) return null;
  const shapeId = String(entry.shapeId || '').trim();
  if (shapeId) {
    const byId = editor.getShape(shapeId);
    if (byId) return byId;
  }
  const activityId = String(entry.activityId || '').trim();
  if (!activityId) return null;
  return (editor.getCurrentPageShapes?.() || []).find(
    shape => shape?.type === 'pin-card' && String(shape.meta?.activityId || '') === activityId
  ) || null;
}

/**
 * Screen-space status label above an object while post-add flows run
 * (enrich, location, digest, done-fill).
 */
export default function ObjectProcessingOverlay() {
  const editor = useEditor();
  const objectProcessing = useBoardStore(state => state.objectProcessing);
  const { geminiLanguage } = useGeminiLanguage();
  const messages = useMemo(() => getCanvasMessages(geminiLanguage), [geminiLanguage]);

  const items = useValue(
    'object-processing-overlay-items',
    () => {
      editor.getCamera?.();
      const viewport = editor.getViewportScreenBounds?.();
      const entries = Object.values(objectProcessing || {});
      if (!entries.length) return [];

      const next = [];
      for (const entry of entries) {
        const text = objectProcessingLabel(entry?.phase, messages);
        if (!text) continue;
        const shape = resolveProcessingShape(editor, entry);
        if (!shape) continue;
        const bounds = editor.getShapePageBounds(shape);
        if (!bounds) continue;

        const screen = editor.pageToScreen({
          x: bounds.x + bounds.w / 2,
          y: bounds.y,
        });

        if (viewport) {
          if (
            screen.x < viewport.x - VIEW_PAD
            || screen.x > viewport.x + viewport.w + VIEW_PAD
            || screen.y < viewport.y - VIEW_PAD
            || screen.y > viewport.y + viewport.h + VIEW_PAD
          ) {
            continue;
          }
        }

        next.push({
          key: `${entry.activityId || ''}:${entry.shapeId || shape.id}:${entry.phase}`,
          text,
          x: screen.x,
          y: screen.y - LABEL_GAP_SCREEN,
        });
      }
      return next;
    },
    [editor, objectProcessing, messages]
  );

  if (!items.length) return null;

  return (
    <>
      {items.map(item => (
        <Box
          key={item.key}
          className="board-object-processing-wrap"
          sx={{
            position: 'fixed',
            left: item.x,
            top: item.y,
            transform: 'translate(-50%, -100%)',
            zIndex: 14,
            pointerEvents: 'none',
          }}
        >
          <span className="board-object-processing-label">
            <span className="board-object-processing-spinner" aria-hidden="true" />
            {item.text}
          </span>
        </Box>
      ))}
    </>
  );
}
